import { useRequiredContext } from "@redotech/react-util/context";
import { useWindowEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  CompensationMethodType,
  DraftReturn,
} from "@redotech/redo-model/draft-return/draft-return";
import { NewExchangeItem } from "@redotech/redo-model/draft-return/draft-return-items";
import { compareMaybeGids } from "@redotech/redo-model/shopify/gid-utils";
import { ExchangesSelection } from "@redotech/redo-model/team";
import * as linkCss from "@redotech/redo-web/link.module.css";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import { ProductInfo } from "@redotech/shopify-storefront";
import * as classNames from "classnames";
import { memo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { AdvancedExchange } from "./components/advanced-exchange";
import * as newItemsStyles from "./new-items.module.css";

export type ExchangeContext = {
  window?: Window | null;
  selectedProduct?: ProductInfo;
};

const exchangeItemsSchema = z.array(
  z.object({
    id: z.union([z.string(), z.number()]),
    quantity: z.number(),
    properties: z.record(z.string(), z.string()).nullish(),
  }),
);

const ExchangeSelectCompleteSchema = z.object({
  type: z.literal("redo.exchange-select.complete"),
  items: exchangeItemsSchema,
  cartToken: z.string().nullish(),
});

type NewItem = z.infer<typeof ExchangeSelectCompleteSchema>["items"][number];

export const NewItems = memo(function NewItems({
  ctx,
  settings,
  draftReturn,
  setDraftReturn,
  onComplete,
}: {
  ctx: ExchangeContext;
  settings: ReturnAppSettings;
  draftReturn: DraftReturn;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  onComplete: () => void;
}) {
  const client = useRequiredContext(RpcClientContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const itemsRef = useRef<NewItem[]>();
  const cartTokenRef = useRef<string | null | undefined>(null);
  const [addItemsLoad, addItems] = useTriggerLoad(async (signal) => {
    if (!itemsRef.current) {
      return;
    }
    const items = itemsRef.current;
    const response = await client.addNewItems(
      {
        draftReturnId: draftReturn._id,
        items: items.reduce<NewExchangeItem[]>((acc, item) => {
          const productId = draftReturn.newOrderData?.items.find((i) =>
            compareMaybeGids(i.variantId, item.id),
          )?.productId;
          for (let i = 0; i < item.quantity; i++) {
            acc.push({
              variantId: String(item.id),
              productId: String(productId),
              quantity: 1,
              properties: item.properties,
            });
          }
          return acc;
        }, []),
        clearExistingItems: true,
        cartToken: cartTokenRef.current,
      },
      { signal },
    );
    setDraftReturn(response.draftReturn);
    onComplete();
  });

  const windowRef = useRef<Window | undefined>();
  const shopOnSite = useHandler((window?: Window) => {
    let path = "";
    const params = new URLSearchParams(settings.exchanges.shopSiteParams ?? {});
    const exchangeValue = draftReturn.compensationMethods
      .find((method) => method.type === CompensationMethodType.EXCHANGE)
      ?.totalReturnableValue.toFixed(2);
    if (exchangeValue) {
      params.set("exchangeValue", exchangeValue);
    }
    if (settings.exchanges.shopSitePath) {
      path = settings.exchanges.shopSitePath;
    } else {
      path = "/apps/redo/exchange-select";
      const productUrl = ctx.selectedProduct
        ? ctx.selectedProduct.onlineStoreUrl ||
          `/products/${ctx.selectedProduct.handle}`
        : null;
      // If they have a custom storefront URL, make sure we send them there.
      if (settings.exchanges.shopSiteURL) {
        params.set("productUrl", settings.exchanges.shopSiteURL);
      } else if (productUrl) {
        params.set("productUrl", productUrl);
      }
      if (settings.exchanges.shopSiteOneTab) {
        params.set("draftReturnId", draftReturn._id);
        const completeExchangeUrl = new URL(location.href);
        completeExchangeUrl.searchParams.set("exchangeComplete", "true");
        params.set("completeExchangeUrl", completeExchangeUrl.toString());
      }
    }

    const url = new URL(
      path + "?" + params.toString(),
      `https://${settings.exchanges.shopSiteDomain || settings.storeUrl}`,
    );
    if (settings.exchanges.shopSiteOneTab) {
      location.href = url.toString();
    } else {
      if (!window) {
        window = globalThis.open(url, "_blank") ?? undefined;
        if (!window) {
          return;
        }
      }
      window.location.href = url.toString();
      windowRef.current = window;
    }
  });

  useEffect(() => {
    if (ctx.window) {
      shopOnSite(ctx.window);
      return () => {
        ctx.window?.close();
      };
    }
    return;
  }, [ctx.window, shopOnSite]);

  useEffect(() => {
    if (
      settings.exchanges.selection === ExchangesSelection.STORE_WEBSITE &&
      settings.exchanges.shopSiteOneTab
    ) {
      try {
        const completeData = searchParams.get("exchange-complete");
        if (completeData) {
          const cartToken = searchParams.get("cartToken");
          if (cartToken) {
            cartTokenRef.current = cartToken;
          }
          const items = exchangeItemsSchema.parse(JSON.parse(completeData));
          if (items.length) {
            setSearchParams({}, { replace: true });
            itemsRef.current = items;
            addItems();
            return;
          }
        }
      } catch (e) {
        console.error(e);
      }

      shopOnSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWindowEventListener(window, "message", async (event) => {
    if (
      event.source === windowRef.current &&
      event.data.type === "redo.exchange-select.complete"
    ) {
      try {
        const data = ExchangeSelectCompleteSchema.parse(event.data);
        itemsRef.current = data.items;
        cartTokenRef.current = data.cartToken;
        addItems();
      } catch (e) {
        console.error(e);
      } finally {
        windowRef.current?.close();
      }
    }
  });

  if (settings.exchanges.selection === ExchangesSelection.STORE_WEBSITE) {
    const numItems = itemsRef.current
      ? itemsRef.current.reduce((acc, item) => acc + item.quantity, 0)
      : null;
    // Shop on site
    return (
      <div className={newItemsStyles.shopOnSite}>
        <div className={newItemsStyles.spinnerContainer}>
          <Spinner style={SpinnerStyle.DARK} />
        </div>
        <div className={newItemsStyles.shopOnSiteText}>
          {addItemsLoad.pending ? (
            <>
              {numItems === 1 ? (
                <div>Adding new item to exchange</div>
              ) : typeof numItems === "number" ? (
                <div>Adding {numItems} new items to exchange</div>
              ) : (
                <div>Adding items to return</div>
              )}
            </>
          ) : (
            <>
              <div>Redirecting to store...</div>
              <div className={newItemsStyles.shopOnSiteRedirectText}>
                Click{" "}
                <span
                  className={classNames(linkCss.link, linkCss.contrastLow)}
                  onClick={() => shopOnSite()}
                >
                  this link
                </span>{" "}
                if you're not automatically redirected
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <AdvancedExchange
      client={client}
      draftReturn={draftReturn}
      exchangeContext={ctx}
      onComplete={onComplete}
      setDraftReturn={setDraftReturn}
      settings={settings}
    />
  );
});
